import React, { useState } from 'react';
import {
  CheckCircle2,
  SkipForward,
  Wrench,
  BellOff,
  Clock,
  AlertCircle,
  CalendarClock,
  Lock,
  ChevronDown,
  Timer,
  DollarSign,
  Package,
  MapPin,
  ShieldAlert,
  History,
} from 'lucide-react';
import { Button } from '../ui';
import { cn } from '../../lib/utils';
import { ScheduledTask } from '../../services/maintenance.api';
import { SnoozeMenu } from './SnoozeMenu';

interface Props {
  task: ScheduledTask;
  onDone: (taskId: string, performedAt: string) => Promise<void>;
  onSkip: (taskId: string) => void;
  onSnooze: (taskId: string, snoozedUntil: string) => Promise<void>;
  onDIY: (task: ScheduledTask) => void;
  onOrderParts: (task: ScheduledTask) => void;
}

function dueDateLabel(dueDate: string, status: string): { text: string; urgent: boolean } {
  const due = new Date(dueDate);
  const now = new Date();
  const diffDays = Math.round((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (status === 'overdue') {
    const overdueDays = Math.abs(diffDays);
    if (overdueDays === 0) return { text: 'Due today', urgent: true };
    const suffix = overdueDays === 1 ? '' : 's';
    return { text: `${overdueDays} day${suffix} overdue`, urgent: true };
  }
  if (diffDays === 0) return { text: 'Due today', urgent: true };
  if (diffDays === 1) return { text: 'Due tomorrow', urgent: false };
  if (diffDays <= 7) return { text: `In ${diffDays} days`, urgent: false };
  return {
    text: due.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    urgent: false,
  };
}

async function executeTaskDone(
  taskId: string,
  dueDate: string,
  intervalDays: number | null | undefined,
  onDone: (id: string, at: string) => Promise<void>,
  setCompleting: (v: boolean) => void,
  setJustCompleted: (v: boolean) => void,
  setNextDueLabel: (v: string | null) => void,
): Promise<void> {
  setCompleting(true);
  try {
    await onDone(taskId, new Date().toISOString());
    const nextDate = new Date(dueDate);
    nextDate.setDate(nextDate.getDate() + (intervalDays ?? 30));
    setNextDueLabel(nextDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
    setJustCompleted(true);
    setTimeout(() => { setJustCompleted(false); setNextDueLabel(null); }, 3500);
  } catch {
    // Parent shows error toast
  } finally {
    setCompleting(false);
  }
}

async function executeTaskSnooze(
  taskId: string,
  until: string,
  onSnooze: (id: string, until: string) => Promise<void>,
  setShowSnooze: (v: boolean) => void,
): Promise<void> {
  setShowSnooze(false);
  try {
    await onSnooze(taskId, until);
  } catch {
    // Parent shows error toast
  }
}

export function MaintenanceTaskCard({ task, onDone, onSkip, onSnooze, onDIY, onOrderParts }: Readonly<Props>) {
  const [expanded, setExpanded] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [showSnooze, setShowSnooze] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  const [nextDueLabel, setNextDueLabel] = useState<string | null>(null);

  const isOverdue = task.status === 'overdue';
  const isCompleted = task.status === 'completed' || justCompleted;
  const isSkipped = task.status === 'skipped';
  const isSnoozed = task.status === 'snoozed';
  const isComingSoon = task.comingSoon;

  const { text: dueDateText, urgent } = dueDateLabel(task.dueDate, task.status);

  const doneLabel = completing ? 'Saving\u2026' : (isCompleted ? 'Done!' : 'Mark Done');
  const dueDateClass = urgent ? 'text-red-500 dark:text-red-400' : 'text-zinc-400 dark:text-zinc-500';

  const handleDone = () => executeTaskDone(task.id, task.dueDate, task.intervalDays, onDone, setCompleting, setJustCompleted, setNextDueLabel);
  const handleSnooze = (until: string) => executeTaskSnooze(task.id, until, onSnooze, setShowSnooze);

  const statusBar = isOverdue
    ? 'bg-red-500'
    : isSnoozed
    ? 'bg-zinc-300 dark:bg-zinc-600'
    : isCompleted
    ? 'bg-green-500'
    : isSkipped
    ? 'bg-amber-400'
    : 'bg-[hsl(var(--brand-primary))]';

  return (
    <div
      className={cn(
        'relative rounded-lg border bg-white dark:bg-zinc-900 overflow-hidden transition-shadow',
        expanded ? 'shadow-sm' : 'hover:shadow-sm',
        isOverdue && 'border-red-200 dark:border-red-900',
        isSnoozed && 'opacity-75 border-zinc-200 dark:border-zinc-800',
        isCompleted && 'opacity-60 border-green-200 dark:border-green-900',
        isSkipped && 'opacity-60 border-amber-200 dark:border-amber-900',
        !isOverdue && !isSnoozed && !isCompleted && !isSkipped && 'border-zinc-200 dark:border-zinc-800',
      )}
    >
      {/* Left status stripe */}
      <div className={cn('absolute left-0 top-0 bottom-0 w-0.5', statusBar)} />

      {/* ── Collapsed header — always visible, click to expand ── */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left pl-4 pr-3 pt-3 pb-3 flex items-start justify-between gap-3 focus:outline-none"
      >
        <div className="flex-1 min-w-0">
          {/* Status badge + due date row */}
          <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
            {isOverdue && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 dark:bg-red-950/40 px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:text-red-400">
                <AlertCircle className="h-2.5 w-2.5" />
                Overdue
              </span>
            )}
            {isSnoozed && (
              <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 text-[11px] font-medium text-zinc-500 dark:text-zinc-400">
                <BellOff className="h-2.5 w-2.5" />
                Snoozed
              </span>
            )}
            {isCompleted && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 dark:bg-green-950/40 px-2 py-0.5 text-[11px] font-medium text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-2.5 w-2.5" />
                {justCompleted ? 'Done!' : 'Completed'}
              </span>
            )}
            {isSkipped && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-950/40 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                <SkipForward className="h-2.5 w-2.5" />
                Skipped
              </span>
            )}
            {isComingSoon && (
              <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 text-[11px] font-medium text-zinc-500 dark:text-zinc-400">
                <Lock className="h-2.5 w-2.5" />
                Coming Soon
              </span>
            )}
            {!isOverdue && !isSnoozed && !isCompleted && !isSkipped && !isComingSoon && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--brand-primary)/0.08)] dark:bg-[hsl(var(--brand-primary)/0.15)] px-2 py-0.5 text-[11px] font-medium text-[hsl(var(--brand-primary))]">
                <CalendarClock className="h-2.5 w-2.5" />
                Upcoming
              </span>
            )}
            <span className={cn('text-[11px] font-medium ml-auto shrink-0', dueDateClass)}>
              {dueDateText}
            </span>
          </div>

          {/* Priority badge */}
          {task.priority && (
            <span className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold mr-1 mb-1',
              task.priority === 'high' && 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400',
              task.priority === 'medium' && 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
              task.priority === 'low' && 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400',
            )}>
              {task.priority === 'high' ? '● High' : task.priority === 'medium' ? '● Medium' : '● Low'}
            </span>
          )}

          {/* Title */}
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 leading-snug">
            {task.title}
          </h3>

          {/* Asset location */}
          {task.assetLocation && (
            <p className="inline-flex items-center gap-1 text-[11px] text-zinc-400 dark:text-zinc-500 mt-0.5">
              <MapPin className="h-2.5 w-2.5 shrink-0" />
              {task.assetLocation}
            </p>
          )}

          {/* whyItMatters preview — one-line teaser on collapsed card */}
          {!expanded && task.whyItMatters && (
            <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mt-0.5 line-clamp-1">
              {task.whyItMatters}
            </p>
          )}
        </div>

        {/* Expand chevron */}
        <span className="shrink-0 mt-0.5 text-zinc-400 dark:text-zinc-500 transition-transform duration-200"
          style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          <ChevronDown className="h-4 w-4" />
        </span>
      </button>

      {/* ── Expandable body ── */}
      {expanded && (
        <div className="pl-4 pr-3 pb-3 border-t border-zinc-100 dark:border-zinc-800 pt-3 space-y-3">

          {/* Meta: time + cost + risk + snooze resume + last done */}
          <div className="flex items-center gap-3 text-[11px] text-zinc-400 dark:text-zinc-500 flex-wrap">
            {!!task.estimatedMinutes && (
              <span className="flex items-center gap-1">
                <Timer className="h-3 w-3" />
                ~{task.estimatedMinutes} min
              </span>
            )}
            {task.estimatedCost && (
              <span className="flex items-center gap-1">
                <DollarSign className="h-3 w-3" />
                {task.estimatedCost}
              </span>
            )}
            {task.priority && (
              <span className="flex items-center gap-1">
                <ShieldAlert className="h-3 w-3" />
                Risk: {task.priority === 'high' ? '8' : task.priority === 'medium' ? '5' : '2'}/10
              </span>
            )}
            {task.lastCompletedAt && (
              <span className="flex items-center gap-1">
                <History className="h-3 w-3" />
                Last done: {(() => {
                  const diff = Math.floor((Date.now() - new Date(task.lastCompletedAt).getTime()) / (1000 * 60 * 60 * 24));
                  if (diff === 0) return 'today';
                  if (diff === 1) return 'yesterday';
                  if (diff < 30) return `${diff} days ago`;
                  if (diff < 365) return `${Math.floor(diff / 30)} months ago`;
                  return `${Math.floor(diff / 365)} year${Math.floor(diff / 365) > 1 ? 's' : ''} ago`;
                })()}
              </span>
            )}
            {isSnoozed && task.snoozedUntil && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Resumes {new Date(task.snoozedUntil).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>

          {/* Description + why it matters */}
          {(task.whyItMatters || task.description || task.difficultyLevel) && (
            <div className="space-y-2">
              {task.whyItMatters && (
                <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed border-l-2 border-amber-300 pl-2">
                  <span className="font-medium text-zinc-700 dark:text-zinc-300">Why it matters: </span>
                  {task.whyItMatters}
                </p>
              )}
              {task.description && (
                <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
                  {task.description}
                </p>
              )}
              {task.difficultyLevel && (
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Difficulty: <span className="font-medium capitalize">{task.difficultyLevel}</span>
                </p>
              )}
              {/* Category + Frequency chips */}
              <div className="flex items-center gap-2 flex-wrap pt-0.5">
                {task.category && (
                  <span className="inline-flex items-center rounded-full bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 text-[11px] text-zinc-500 dark:text-zinc-400 capitalize">
                    {task.category}
                  </span>
                )}
                {task.frequency && (
                  <span className="inline-flex items-center rounded-full bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 text-[11px] text-zinc-500 dark:text-zinc-400 capitalize">
                    Every {task.frequency.replace('bi-annual', '6 months').replace('biennial', '2 years').replace('annual', 'year').replace('quarterly', '3 months').replace('monthly', 'month')}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Just-completed feedback */}
          {justCompleted && nextDueLabel && (
            <div className="rounded-md bg-green-50 dark:bg-green-950/20 border border-green-100 dark:border-green-900/30 px-3 py-2 flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600 dark:text-green-400 shrink-0" />
              <p className="text-xs text-green-800 dark:text-green-300">
                Next reminder: <span className="font-semibold">{nextDueLabel}</span>
              </p>
            </div>
          )}

          {/* Action buttons */}
          {!isCompleted && !isSkipped && !isComingSoon && (
            <div className="flex items-center gap-2 flex-wrap pt-1">
              <Button
                size="sm"
                onClick={handleDone}
                disabled={completing}
                className="bg-green-600 hover:bg-green-700 text-white border-0 text-xs h-7 px-3"
              >
                <CheckCircle2 className="h-3 w-3 mr-1" />
                {doneLabel}
              </Button>

              <Button
                size="sm"
                variant="outline"
                onClick={() => onSkip(task.id)}
                className="text-xs h-7 px-3 text-zinc-600 dark:text-zinc-400"
              >
                <SkipForward className="h-3 w-3 mr-1" />
                Skip
              </Button>

              <div className="relative">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowSnooze((v) => !v)}
                  className="text-xs h-7 px-3 text-zinc-600 dark:text-zinc-400"
                >
                  <BellOff className="h-3 w-3 mr-1" />
                  Snooze
                </Button>
                {showSnooze && (
                  <SnoozeMenu onSelect={handleSnooze} onClose={() => setShowSnooze(false)} />
                )}
              </div>

              {task.diyAvailable && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDIY(task)}
                    className="text-xs h-7 px-3 text-[hsl(var(--brand-primary))] border-[hsl(var(--brand-primary)/0.3)]"
                  >
                    <Wrench className="h-3 w-3 mr-1" />
                    DIY Guide
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onOrderParts(task)}
                    className="text-xs h-7 px-3 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800"
                  >
                    <Package className="h-3 w-3 mr-1" />
                    Order Parts
                  </Button>
                </>
              )}
            </div>
          )}

          {isComingSoon && (
            <div className="rounded-md bg-zinc-50 dark:bg-zinc-800/60 border border-dashed border-zinc-200 dark:border-zinc-700 px-3 py-2 flex items-center gap-2 text-xs text-zinc-400 dark:text-zinc-500">
              <Lock className="h-3.5 w-3.5" />
              DIY guide coming soon
            </div>
          )}
        </div>
      )}
    </div>
  );
}
